/**
 * The launch chapter's own perimeter, read from the URL.
 *
 * `/api/v1/launch/versions` takes `option_id`, and the platform's G16 fix made that the
 * whole point of the endpoint rather than a filter on it: the previous signature could
 * only ever have answered from an unbounded read over every allocation version in the
 * estate, which is why its selector had no caller. So this chapter needs an option
 * identifier before it can ask the client anything, and it needs one that is **not** a
 * default — a hard-coded fallback here would rebuild the unbounded read one level up.
 *
 * Where it comes from is already decided. G2.2: *"THE URL IS THE STATE. Any screen,
 * filter set, drill path and scenario is a shareable, bookmarkable link."* The option is
 * a filter, so it lives in the search string as `?option=…` and nowhere else.
 *
 * It is read here rather than taken from the router because `src/app/` is upstream of
 * every feature — `no-restricted-imports` makes `@/app/*` a build failure from a feature
 * module, and the dependency direction that rule protects is the right one. Reading
 * `window.location.search` through `useSyncExternalStore` gives the same answer, stays
 * subscribed to back/forward, and keeps the shell composing features rather than the
 * reverse.
 *
 * THE DIRECTORY NOW EXISTS. This paragraph used to say the pinned spec had no option
 * directory, so a screen opened with no option could only render an empty state naming
 * the address bar — a request no planner can act on (audit v5 N-01). The platform has since
 * published `GET /api/v1/launch/options` for exactly this, and `./OptionPicker.tsx` reads it.
 * Nothing defaults: the planner picks, and `setOptionId` writes the choice here, to the URL.
 */

import { useSyncExternalStore } from 'react';

/** The search parameter carrying the option. One name, used by every launch screen. */
export const OPTION_PARAM = 'option';

/** No option selected. Deliberately not a valid identifier — it must never reach a query. */
export const NO_OPTION = '';

function subscribe(onChange: () => void): () => void {
  window.addEventListener('popstate', onChange);
  return () => window.removeEventListener('popstate', onChange);
}

function snapshot(): string {
  if (typeof window === 'undefined') return NO_OPTION;
  return new URLSearchParams(window.location.search).get(OPTION_PARAM) ?? NO_OPTION;
}

/** The server snapshot. There is no URL to read during SSR, and no option to guess. */
function serverSnapshot(): string {
  return NO_OPTION;
}

/**
 * Write the chosen option onto the address bar, keeping every other parameter.
 *
 * `pushState` plus a manual `popstate`, as `exit/scope.ts` and `inseason/scope.ts` do: the
 * browser fires no event for `pushState`, and `useOptionId` below would never learn of it.
 */
export function setOptionId(value: string): void {
  if (typeof window === 'undefined') return;
  const next = new URLSearchParams(window.location.search);
  if (value.trim() === '') next.delete(OPTION_PARAM);
  else next.set(OPTION_PARAM, value);
  const query = next.toString();
  window.history.pushState({}, '', query === '' ? window.location.pathname : `?${query}`);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/**
 * The option this screen is scoped to, or `NO_OPTION`.
 *
 * A caller must gate its query on the empty case rather than substituting anything for
 * it: `useApiQuery(..., { enabled: optionId !== NO_OPTION })`.
 */
export function useOptionId(): string {
  return useSyncExternalStore(subscribe, snapshot, serverSnapshot);
}
